import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  EventBusy as LeaveIcon,
  Add as AddIcon,
  Delete as CancelIcon,
  CalendarMonth as CalendarIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckIcon,
  Cancel as XIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { RootState } from '../../store';
import axios from 'axios';

interface Leave {
  id: number;
  doctorId: number;
  doctorName: string;
  leaveDate: string;
  endDate: string;
  leaveType: string;
  leaveTypeName: string;
  leaveTypeNameHindi: string;
  reason: string;
  status: string;
  statusName: string;
  statusNameHindi: string;
  isFullDay: boolean;
  notes: string;
  createdAt: string;
  canBeCancelled: boolean;
  isPast: boolean;
}

interface LeaveListResponse {
  leaves: Leave[];
  totalLeaves: number;
  upcomingLeaves: number;
  pastLeaves: number;
  cancelledLeaves: number;
}

const LEAVE_TYPES = [
  { value: 'PERSONAL', label: 'Personal', hindi: 'व्यक्तिगत' },
  { value: 'MEDICAL', label: 'Medical', hindi: 'चिकित्सा' },
  { value: 'EMERGENCY', label: 'Emergency', hindi: 'आपातकालीन' },
  { value: 'VACATION', label: 'Vacation', hindi: 'छुट्टी' },
  { value: 'TRAINING', label: 'Training', hindi: 'प्रशिक्षण' },
  { value: 'OTHER', label: 'Other', hindi: 'अन्य' },
];

/**
 * Doctor Leave Management Page
 * Manage leave dates and blocked slots
 */
const DoctorLeavePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [leaveData, setLeaveData] = useState<LeaveListResponse | null>(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Dialog states
  const [openLeaveDialog, setOpenLeaveDialog] = useState(false);
  const [openMultiDayDialog, setOpenMultiDayDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState<Leave | null>(null);

  // Form states
  const [leaveDate, setLeaveDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [leaveType, setLeaveType] = useState('PERSONAL');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'GOVERNMENT_DOCTOR') {
      navigate('/login');
      return;
    }
    fetchLeaves();
  }, [isAuthenticated, user, navigate]);

  const fetchLeaves = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/doctor/leaves`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setLeaveData(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch leaves:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyLeave = async () => {
    if (!leaveDate || !reason) {
      setErrorMessage('Please fill all required fields');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/doctor/leaves`,
        {
          leaveDate,
          leaveType,
          reason,
          notes,
          isFullDay: true,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccessMessage(`Leave applied for ${leaveDate}. All slots blocked.`);
      setOpenLeaveDialog(false);
      resetForm();
      fetchLeaves();
    } catch (error: any) {
      setErrorMessage(error.response?.data?.message || 'Failed to apply leave');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyMultiDayLeave = async () => {
    if (!leaveDate || !endDate || !reason) {
      setErrorMessage('Please fill all required fields');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/doctor/leaves/multi-day`,
        {
          leaveDate,
          endDate,
          leaveType,
          reason,
          notes,
          isFullDay: true,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccessMessage(`Leave applied from ${leaveDate} to ${endDate}. All slots blocked.`);
      setOpenMultiDayDialog(false);
      resetForm();
      fetchLeaves();
    } catch (error: any) {
      setErrorMessage(error.response?.data?.message || 'Failed to apply leave');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelLeave = async () => {
    if (!selectedLeave) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/doctor/leaves/${selectedLeave.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccessMessage(`Leave cancelled for ${selectedLeave.leaveDate}. Slots are now available.`);
      setOpenCancelDialog(false);
      setSelectedLeave(null);
      fetchLeaves();
    } catch (error: any) {
      setErrorMessage(error.response?.data?.message || 'Failed to cancel leave');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setLeaveDate('');
    setEndDate('');
    setLeaveType('PERSONAL');
    setReason('');
    setNotes('');
    setErrorMessage('');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED': return 'success';
      case 'PENDING': return 'warning';
      case 'CANCELLED': return 'default';
      case 'REJECTED': return 'error';
      default: return 'default';
    }
  };

  const getLeaveTypeColor = (type: string) => {
    switch (type) {
      case 'MEDICAL': return '#d32f2f';
      case 'EMERGENCY': return '#ed6c02';
      case 'VACATION': return '#0288d1';
      case 'PERSONAL': return '#2e7d32';
      case 'TRAINING': return '#7b1fa2';
      default: return '#757575';
    }
  };

  // Get today's date for min date
  const today = new Date().toISOString().split('T')[0];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: 'transparent' , minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b' }}>
            Leave Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Mark yourself unavailable and automatically block appointment slots
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<CalendarIcon />}
            onClick={() => setOpenMultiDayDialog(true)}
          >
            Multi-Day Leave
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<AddIcon />}
            onClick={() => setOpenLeaveDialog(true)}
          >
            Apply Leave
          </Button>
        </Box>
      </Box>

      {/* Messages */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      )}

      {/* Summary Cards */}
      {leaveData && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                {/* HIGH CONTRAST: Light background + Dark icon */}
                <Avatar sx={{ bgcolor: 'transparent' , color: '#1D4ED8', mx: 'auto', mb: 1, border: '2px solid rgba(29, 78, 216, 0.2)' }}>
                  <LeaveIcon />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A1A2E' }}>{leaveData.totalLeaves}</Typography>
                <Typography variant="body2" color="text.secondary">Total Leaves</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                {/* HIGH CONTRAST: Light background + Dark icon */}
                <Avatar sx={{ bgcolor: 'transparent' , color: '#15803D', mx: 'auto', mb: 1, border: '2px solid rgba(21, 128, 61, 0.2)' }}>
                  <CalendarIcon />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A1A2E' }}>{leaveData.upcomingLeaves}</Typography>
                <Typography variant="body2" color="text.secondary">Upcoming</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                {/* HIGH CONTRAST: Light background + Dark icon */}
                <Avatar sx={{ bgcolor: 'transparent' , color: '#B45309', mx: 'auto', mb: 1, border: '2px solid rgba(180, 83, 9, 0.2)' }}>
                  <HistoryIcon />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A1A2E' }}>{leaveData.pastLeaves}</Typography>
                <Typography variant="body2" color="text.secondary">Past Leaves</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                {/* HIGH CONTRAST: Light background + Dark icon */}
                <Avatar sx={{ bgcolor: 'transparent' , color: '#DC2626', mx: 'auto', mb: 1, border: '2px solid rgba(220, 38, 38, 0.2)' }}>
                  <XIcon />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A1A2E' }}>{leaveData.cancelledLeaves}</Typography>
                <Typography variant="body2" color="text.secondary">Cancelled</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="subtitle2">When you apply for leave:</Typography>
        <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
          <li>All appointment slots for that date will be automatically blocked</li>
          <li>No new appointments can be booked for blocked dates</li>
          <li>You can cancel leave to make slots available again</li>
        </ul>
      </Alert>

      {/* Leave History Table */}
      <Paper sx={{ borderRadius: 2 }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon color="primary" />
            Leave History
          </Typography>
        </Box>
        <Divider />
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leaveData?.leaves.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No leaves found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                leaveData?.leaves.map((leave) => (
                  <TableRow
                    key={leave.id}
                    sx={{
                      bgcolor: leave.isPast ? '#f5f5f5' : 'inherit',
                      opacity: leave.status === 'CANCELLED' ? 0.6 : 1,
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {new Date(leave.leaveDate).toLocaleDateString('en-IN', {
                            weekday: 'short',
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </Typography>
                        {leave.isPast && (
                          <Chip label="Past" size="small" color="default" sx={{ mt: 0.5 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={leave.leaveTypeName}
                        size="small"
                        sx={{
                          bgcolor: `${getLeaveTypeColor(leave.leaveType)}20`,
                          color: getLeaveTypeColor(leave.leaveType),
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200 }}>
                        {leave.reason}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={leave.statusName}
                        size="small"
                        color={getStatusColor(leave.status) as any}
                      />
                    </TableCell>
                    <TableCell>
                      {leave.canBeCancelled ? (
                        <Button
                          size="small"
                          color="error"
                          startIcon={<CancelIcon />}
                          onClick={() => {
                            setSelectedLeave(leave);
                            setOpenCancelDialog(true);
                          }}
                        >
                          Cancel
                        </Button>
                      ) : leave.status === 'CANCELLED' ? (
                        <Typography variant="caption" color="text.secondary">
                          Cancelled
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Apply Leave Dialog */}
      <Dialog open={openLeaveDialog} onClose={() => { setOpenLeaveDialog(false); resetForm(); }} maxWidth="sm" fullWidth>
        <DialogTitle>Apply for Leave</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              type="date"
              label="Leave Date *"
              value={leaveDate}
              onChange={(e) => setLeaveDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: today }}
            />

            <FormControl fullWidth>
              <InputLabel>Leave Type *</InputLabel>
              <Select
                value={leaveType}
                label="Leave Type *"
                onChange={(e) => setLeaveType(e.target.value)}
              >
                {LEAVE_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label} ({type.hindi})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={2}
              label="Reason *"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for leave..."
            />

            <TextField
              fullWidth
              multiline
              rows={2}
              label="Notes (Optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenLeaveDialog(false); resetForm(); }}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleApplyLeave}
            disabled={saving || !leaveDate || !reason}
          >
            {saving ? <CircularProgress size={20} /> : 'Apply Leave'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Multi-Day Leave Dialog */}
      <Dialog open={openMultiDayDialog} onClose={() => { setOpenMultiDayDialog(false); resetForm(); }} maxWidth="sm" fullWidth>
        <DialogTitle>Apply for Multi-Day Leave</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start Date *"
                  value={leaveDate}
                  onChange={(e) => setLeaveDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: today }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="End Date *"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: leaveDate || today }}
                />
              </Grid>
            </Grid>

            <FormControl fullWidth>
              <InputLabel>Leave Type *</InputLabel>
              <Select
                value={leaveType}
                label="Leave Type *"
                onChange={(e) => setLeaveType(e.target.value)}
              >
                {LEAVE_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label} ({type.hindi})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={2}
              label="Reason *"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for leave..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenMultiDayDialog(false); resetForm(); }}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleApplyMultiDayLeave}
            disabled={saving || !leaveDate || !endDate || !reason}
          >
            {saving ? <CircularProgress size={20} /> : 'Apply Multi-Day Leave'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Leave Dialog */}
      <Dialog open={openCancelDialog} onClose={() => setOpenCancelDialog(false)} maxWidth="sm">
        <DialogTitle>Cancel Leave</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Cancelling this leave will make all slots available for booking again.
          </Alert>
          {selectedLeave && (
            <Box>
              <Typography variant="body2">
                <strong>Date:</strong> {selectedLeave.leaveDate}
              </Typography>
              <Typography variant="body2">
                <strong>Type:</strong> {selectedLeave.leaveTypeName}
              </Typography>
              <Typography variant="body2">
                <strong>Reason:</strong> {selectedLeave.reason}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCancelDialog(false)}>No, Keep Leave</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelLeave}
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : 'Yes, Cancel Leave'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DoctorLeavePage;
